import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { blogAPI } from '../utils/api';
import { setSEO } from '../utils/seo';
import AdUnit from '../components/AdUnit';
import BannerSlider from '../components/BannerSlider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getInitials = (name) => {
  if (!name) return 'WV';
  return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
};

const TAG_COLORS = {
  'phu quoc': { bg: '#fde8e7', color: '#a31712' },
  'food': { bg: '#fff4d4', color: '#7a5b00' },
  'hoi an': { bg: '#e4f2ea', color: '#1a6e45' },
  'sapa': { bg: '#e4f2ea', color: '#1a6e45' },
  'dining': { bg: '#fff4d4', color: '#7a5b00' },
};

const getTagStyle = (tag) => {
  const key = (tag || '').toLowerCase();
  return TAG_COLORS[key] || { bg: '#f3efe6', color: '#6b6660' };
};

import LocationPopup from '../components/LocationPopup';

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      blogAPI.getOne(id),
      blogAPI.getAll(true),
    ]).then(([postRes, allRes]) => {
      setPost(postRes.data);
      setAllPosts(allRes.data || []);
      setLoading(false);
      const p = postRes.data;
      setSEO({
        title: `${p.title} | Benz Eatery Blog — Phu Quoc & Vietnam Guide`,
        description: p.excerpt || `${p.title} — Read on Wander Vietnam by Benz Eatery. Travel guides, food stories & tips for Phu Quoc and Vietnam.`,
        ogType: 'article',
        canonical: `https://benzeatery.com/blog/${id}`,
      });
    }).catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  const shareUrl = `https://benzeatery.com/blog/${id}`;
  const shareTitle = post?.title || '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  // Related posts: same tags, excluding current, max 3
  const relatedPosts = allPosts
    .filter(p => p.id !== id)
    .map(p => ({
      ...p,
      score: (p.tags || []).filter(t => (post?.tags || []).includes(t)).length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  if (loading) {
    return (
      <div className="wv-blog-loading">
        <div className="wv-spinner" />
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="wv-blog-loading">
        <p style={{ color: '#6b6660', fontSize: 18 }}>Post not found.</p>
        <Link to="/" style={{ color: '#da251d', fontSize: 14, fontWeight: 500, marginTop: 12, display: 'inline-block' }}>← Back to Home</Link>
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      </div>
    );
  }

  const coverSrc = post.cover_image
    ? (post.cover_image.startsWith('/') ? `${API_URL}${post.cover_image}` : post.cover_image)
    : null;

  return (
    <div className="wv-blog-root" data-testid="blog-detail-page">
      {/* Nav */}
      <nav className="wv-blog-nav">
        <div className="wv-blog-nav-inner">
          <Link to="/" className="wv-blog-logo" data-testid="blog-home-link">
            <div className="wv-blog-logo-mark"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></div>
            <div>
              <span>Wander Vietnam</span>
              <span className="wv-powered-by">Powered by Benz Eatery</span>
            </div>
          </Link>
          <div className="wv-blog-nav-right">
            <Link to="/" className="wv-blog-back-link" data-testid="blog-back-link">← All stories</Link>
            <Link to="/menu" className="wv-blog-menu-btn" data-testid="blog-menu-link">Our Restaurant</Link>
          </div>
        </div>
      </nav>

      {/* Hero cover */}
      {coverSrc && (
        <div className="wv-blog-cover">
          <img src={coverSrc} alt={post.title} />
          <div className="wv-blog-cover-fade" />
        </div>
      )}
      {!coverSrc && <div style={{ height: 80 }} />}

      {/* Article */}
      <article className="wv-blog-article">
        <div className="wv-blog-header">
          <div className="wv-blog-tags">
            {(post.tags || []).map(tag => (
              <span key={tag} className="wv-blog-tag" style={{ background: getTagStyle(tag).bg, color: getTagStyle(tag).color }}>
                {tag}
              </span>
            ))}
          </div>
          <h1 data-testid="blog-post-title">{post.title}</h1>
          <div className="wv-blog-byline">
            <div className="wv-blog-avatar">{getInitials(post.author)}</div>
            <div>
              <div className="wv-blog-author">{post.author || 'Wander Vietnam'}</div>
              <div className="wv-blog-date">
                {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                {post.read_time && ` · ${post.read_time}`}
              </div>
            </div>
          </div>
        </div>

        <div className="wv-blog-body" data-testid="blog-content">
          {post.content && post.content.includes('<') ? (
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          ) : (
            <ReactMarkdown>{post.content}</ReactMarkdown>
          )}
        </div>

        {/* In-house banner: After article content */}
        <BannerSlider position="blog" />
        <AdUnit variant="in-article" />

        {/* Social Sharing */}
        <div className="wv-share" data-testid="blog-share">
          <h4 className="wv-share-title">Share this article</h4>
          <div className="wv-share-buttons">
            {/* Global */}
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-fb" data-testid="share-facebook">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-x" data-testid="share-twitter">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X
            </a>
            <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-wa" data-testid="share-whatsapp">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            <a href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-tg" data-testid="share-telegram">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              Telegram
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-li" data-testid="share-linkedin">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
            {/* Vietnam */}
            <a href={`https://zalo.me/share?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-zalo" data-testid="share-zalo">
              <svg width="16" height="16" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="#0068FF"/><path d="M13 17h12.5l-9.5 12h9v2H13l9.5-12H13v-2zm19 0h3v14h-3V17zm-4.5 4.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" fill="white"/></svg>
              Zalo
            </a>
            <a href={`https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=291494419107518&redirect_uri=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-msg" data-testid="share-messenger">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8.2l3.131 3.259L19.752 8.2l-6.561 6.763z"/></svg>
              Messenger
            </a>
            {/* China */}
            <a href={`https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-weibo" data-testid="share-weibo">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10.098 20.323c-3.977.391-7.414-1.406-7.672-4.02-.259-2.609 2.759-5.047 6.74-5.441 3.979-.394 7.413 1.404 7.671 4.018.259 2.6-2.759 5.049-6.739 5.443zM20.196 9.4a4.04 4.04 0 00-4.652-1.56 1.13 1.13 0 00-.675 1.45 1.13 1.13 0 001.45.675 1.78 1.78 0 012.452 1.3c.236.894-.235 1.812-1.064 2.108a1.13 1.13 0 00-.675 1.45 1.13 1.13 0 001.45.675c1.624-.578 2.604-2.365 2.237-4.098h-.002z"/><path d="M21.986 6.674a6.43 6.43 0 00-7.413-2.483.847.847 0 00-.506 1.088.847.847 0 001.088.506 4.73 4.73 0 015.453 1.826 4.73 4.73 0 01.323 5.33.847.847 0 00.34 1.149.847.847 0 001.149-.34 6.43 6.43 0 00-.434-7.076z"/></svg>
              Weibo
            </a>
            <a href={`https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-qq" data-testid="share-qq">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21.395 15.035a39.548 39.548 0 00-1.287-3.665c0-3.408-1.994-9.37-8.108-9.37-6.114 0-8.108 5.962-8.108 9.37a39.548 39.548 0 00-1.287 3.665c-.645 2.243-.742 4.345.066 4.839.53.322 1.31-.204 2.105-1.156.271.633.636 1.31 1.12 1.918-1.094.365-1.825.919-1.825 1.558 0 1.04 1.897 1.883 4.236 1.883 2.003 0 3.671-.62 4.107-1.464h.572c.436.844 2.104 1.464 4.107 1.464 2.339 0 4.236-.843 4.236-1.883 0-.639-.731-1.193-1.825-1.558.484-.608.849-1.285 1.12-1.918.795.952 1.575 1.478 2.105 1.156.808-.494.711-2.596.066-4.839z"/></svg>
              QQ
            </a>
            <a href={`weixin://dl/moments?url=${encodeURIComponent(shareUrl)}`} className="wv-share-btn wv-share-wechat" data-testid="share-wechat">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm3.785 4.32c-3.956 0-7.169 2.644-7.169 5.906 0 3.265 3.21 5.906 7.17 5.906.772 0 1.517-.107 2.215-.312a.68.68 0 01.56.077l1.49.87a.26.26 0 00.13.043.227.227 0 00.227-.232c0-.057-.023-.11-.038-.165l-.305-1.156a.462.462 0 01.166-.52c1.43-1.051 2.346-2.608 2.346-4.348 0-3.426-3.213-6.07-7.169-6.07h-.623zm-2.55 3.204c.502 0 .908.413.908.921a.914.914 0 01-.908.921.914.914 0 01-.907-.921c0-.508.406-.921.907-.921zm4.55 0c.502 0 .908.413.908.921a.914.914 0 01-.908.921.914.914 0 01-.907-.921c0-.508.406-.921.907-.921z"/></svg>
              WeChat
            </a>
            {/* Russia */}
            <a href={`https://vk.com/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-vk" data-testid="share-vk">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.391 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.862-.525-2.049-1.714-1.033-1.01-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4.03 8.57 4.03 8.096c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.847 2.455 2.27 4.607 2.853 4.607.22 0 .322-.102.322-.66V10.55c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.049.17.49-.085.744-.576.744z"/></svg>
              VK
            </a>
            <a href={`https://connect.ok.ru/offer?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-ok" data-testid="share-ok">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14.505 17.44a11.97 11.97 0 002.46-1.07.75.75 0 00-.762-1.293 10.48 10.48 0 01-4.201 1.31 10.49 10.49 0 01-4.201-1.31.75.75 0 00-.762 1.293c.793.468 1.617.83 2.46 1.07l-3.09 3.09a.75.75 0 001.061 1.06L12 18.06l3.53 3.53a.75.75 0 001.06-1.06l-3.09-3.09h1.005zM12 12.553a4.778 4.778 0 110-9.555 4.778 4.778 0 010 9.555zm0-7.555a2.778 2.778 0 100 5.555 2.778 2.778 0 000-5.555z"/></svg>
              OK.ru
            </a>
            {/* More Global */}
            <a href={`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-pin" data-testid="share-pinterest">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>
              Pinterest
            </a>
            <a href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-reddit" data-testid="share-reddit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 000-.462.342.342 0 00-.465 0c-.533.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z"/></svg>
              Reddit
            </a>
            <a href={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="wv-share-btn wv-share-line" data-testid="share-line">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386a.63.63 0 01-.63-.629V8.108c0-.345.281-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596a.625.625 0 01-.707-.234l-2.444-3.33v2.968a.63.63 0 01-1.26 0V8.108a.631.631 0 011.14-.363l2.444 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771h-.001zm-6.137 0a.63.63 0 01-1.259 0V8.108c0-.345.282-.63.63-.63.346 0 .63.285.63.63v4.771h-.001zM6.4 13.509H4.645a.63.63 0 01-.63-.63V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.141H6.4c.349 0 .63.283.63.63a.627.627 0 01-.63.63M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
              Line
            </a>
            <a href={`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareTitle + '\n\n' + shareUrl)}`} className="wv-share-btn wv-share-email" data-testid="share-email">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              Email
            </a>
            <button onClick={copyLink} className="wv-share-btn wv-share-copy" data-testid="share-copy">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="wv-related" data-testid="blog-related">
          <h3 className="wv-related-title">You might also enjoy</h3>
          <div className="wv-related-grid">
            {relatedPosts.map(rp => (
              <Link to={`/blog/${rp.id}`} key={rp.id} className="wv-related-card" data-testid={`related-${rp.id}`}>
                <div className="wv-related-img">
                  {rp.cover_image ? (
                    <img src={rp.cover_image.startsWith('/') ? `${API_URL}${rp.cover_image}` : rp.cover_image} alt={rp.title} />
                  ) : (
                    <div className="wv-related-ph">{rp.title?.charAt(0)}</div>
                  )}
                </div>
                <div className="wv-related-body">
                  {rp.tags?.[0] && <span className="wv-related-tag">{rp.tags[0]}</span>}
                  <h4>{rp.title}</h4>
                  <p>{rp.excerpt?.substring(0, 100)}...</p>
                  <span className="wv-related-meta">
                    {rp.author || 'Wander Vietnam'} · {new Date(rp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Location Popup */}
      <LocationPopup lang="en" />

      {/* Footer */}
      <footer className="wv-blog-footer">
        <div className="wv-blog-footer-policies">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span className="wv-foot-dot" />
          <Link to="/terms">Terms of Service</Link>
          <span className="wv-foot-dot" />
          <Link to="/cookie-policy">Cookie Policy</Link>
          <span className="wv-foot-dot" />
          <Link to="/disclaimer">Disclaimer</Link>
        </div>
        <div className="wv-blog-footer-inner">
          <span>© {new Date().getFullYear()} Wander Vietnam · Benz Eatery, Phu Quoc</span>
          <span>
            Dev by{' '}
            <a href="https://rigeneconsultancy.com" target="_blank" rel="noopener noreferrer" style={{ color: '#da251d', fontWeight: 500 }}>
              RCS
            </a>
          </span>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
    </div>
  );
};

const STYLES = `
  .wv-blog-root {
    --bg: #fbf9f4;
    --text: #1c1a17;
    --text-muted: #6b6660;
    --text-soft: #9a9590;
    --border: rgba(0,0,0,0.08);
    --accent: #da251d;
    --accent-2: #ffcd00;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: var(--bg);
    color: var(--text);
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
  }
  .wv-blog-root a { color: inherit; text-decoration: none; }

  .wv-blog-loading {
    min-height: 100vh;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    background: #fbf9f4;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .wv-spinner {
    width: 32px; height: 32px;
    border: 2px solid #da251d;
    border-top-color: transparent;
    border-radius: 50%;
    animation: wv-spin 0.8s linear infinite;
  }
  @keyframes wv-spin { to { transform: rotate(360deg); } }

  .wv-blog-nav {
    position: sticky; top: 0; z-index: 50;
    background: rgba(251,249,244,0.9);
    backdrop-filter: blur(12px);
    border-bottom: 0.5px solid var(--border);
  }
  .wv-blog-nav-inner {
    max-width: 900px; margin: 0 auto;
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 32px;
  }
  .wv-blog-logo {
    font-family: Georgia, serif;
    font-size: 20px; font-weight: 500;
    display: flex; align-items: center; gap: 8px;
  }
  .wv-powered-by {
    display: block;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.3px;
    color: var(--text-soft);
    margin-top: -2px;
  }
  .wv-blog-logo-mark {
    width: 28px; height: 28px;
    background: var(--accent);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: var(--accent-2);
    font-size: 14px;
    flex-shrink: 0;
  }
  .wv-blog-nav-right { display: flex; align-items: center; gap: 16px; }
  .wv-blog-back-link { font-size: 13px; color: var(--text-muted); }
  .wv-blog-back-link:hover { color: var(--text); }
  .wv-blog-menu-btn {
    background: var(--accent);
    color: white;
    padding: 7px 16px;
    border-radius: 18px;
    font-size: 13px; font-weight: 500;
    display: inline-block;
  }

  .wv-blog-cover {
    position: relative;
    max-width: 900px; margin: 0 auto;
    padding: 24px 32px 0;
  }
  .wv-blog-cover img {
    width: 100%; height: 420px;
    object-fit: cover;
    border-radius: 16px;
    display: block;
  }
  .wv-blog-cover-fade {
    position: absolute; bottom: 0; left: 32px; right: 32px; height: 80px;
    background: linear-gradient(transparent, var(--bg));
    border-radius: 0 0 16px 16px;
  }

  .wv-blog-article {
    max-width: 720px; margin: 0 auto;
    padding: 32px 32px 60px;
  }
  .wv-blog-header { margin-bottom: 40px; }
  .wv-blog-tags { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
  .wv-blog-tag {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-weight: 500;
  }
  .wv-blog-header h1 {
    font-family: Georgia, serif;
    font-size: 42px;
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -1px;
    margin-bottom: 24px;
  }
  .wv-blog-byline {
    display: flex; align-items: center; gap: 14px;
  }
  .wv-blog-avatar {
    width: 42px; height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ffcd00, #da251d);
    display: flex; align-items: center; justify-content: center;
    color: white; font-size: 14px; font-weight: 500;
    flex-shrink: 0;
  }
  .wv-blog-author { font-weight: 500; font-size: 15px; }
  .wv-blog-date { font-size: 13px; color: var(--text-muted); margin-top: 2px; }

  .wv-blog-body { line-height: 1.8; font-size: 17px; color: #2c2a27; }
  .wv-blog-body h2 {
    font-family: Georgia, serif;
    font-size: 28px; font-weight: 500;
    margin: 2.5em 0 0.5em;
    letter-spacing: -0.3px;
  }
  .wv-blog-body h3 {
    font-family: Georgia, serif;
    font-size: 22px; font-weight: 500;
    margin: 2em 0 0.4em;
  }
  .wv-blog-body p { margin-bottom: 1.5em; }
  .wv-blog-body ul, .wv-blog-body ol { margin-bottom: 1.5em; padding-left: 1.5em; }
  .wv-blog-body li { margin-bottom: 0.5em; }
  .wv-blog-body blockquote {
    border-left: 3px solid var(--accent);
    padding-left: 1.5em;
    margin: 2em 0;
    font-style: italic;
    color: var(--text-muted);
  }
  .wv-blog-body img { max-width: 100%; border-radius: 12px; margin: 2em 0; }
  .wv-blog-body a { color: var(--accent); text-decoration: underline; }
  .wv-blog-body strong { font-weight: 600; }

  /* Social Sharing */
  .wv-share {
    margin-top: 48px;
    padding-top: 32px;
    border-top: 0.5px solid var(--border);
  }
  .wv-share-title {
    font-size: 13px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--text-muted); margin-bottom: 14px;
  }
  .wv-share-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
  .wv-share-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 18px; border-radius: 12px;
    font-size: 13px; font-weight: 600;
    text-decoration: none; border: none; cursor: pointer;
    font-family: inherit; transition: all 0.15s;
  }
  .wv-share-fb { background: #1877f215; color: #1877f2; }
  .wv-share-fb:hover { background: #1877f230; }
  .wv-share-x { background: #0f141915; color: #0f1419; }
  .wv-share-x:hover { background: #0f141925; }
  .wv-share-wa { background: #25d36615; color: #128c7e; }
  .wv-share-wa:hover { background: #25d36630; }
  .wv-share-tg { background: #0088cc15; color: #0088cc; }
  .wv-share-tg:hover { background: #0088cc30; }
  .wv-share-li { background: #0a66c215; color: #0a66c2; }
  .wv-share-li:hover { background: #0a66c230; }
  .wv-share-zalo { background: #0068ff15; color: #0068ff; }
  .wv-share-zalo:hover { background: #0068ff30; }
  .wv-share-msg { background: #006aff15; color: #006aff; }
  .wv-share-msg:hover { background: #006aff30; }
  .wv-share-weibo { background: #e6162d15; color: #e6162d; }
  .wv-share-weibo:hover { background: #e6162d30; }
  .wv-share-qq { background: #1296db15; color: #1296db; }
  .wv-share-qq:hover { background: #1296db30; }
  .wv-share-wechat { background: #09b83e15; color: #09b83e; }
  .wv-share-wechat:hover { background: #09b83e30; }
  .wv-share-vk { background: #0077ff15; color: #0077ff; }
  .wv-share-vk:hover { background: #0077ff30; }
  .wv-share-ok { background: #ee820815; color: #ee8208; }
  .wv-share-ok:hover { background: #ee820830; }
  .wv-share-pin { background: #e6001915; color: #e60019; }
  .wv-share-pin:hover { background: #e6001930; }
  .wv-share-reddit { background: #ff440015; color: #ff4500; }
  .wv-share-reddit:hover { background: #ff440030; }
  .wv-share-line { background: #06c75515; color: #06c755; }
  .wv-share-line:hover { background: #06c75530; }
  .wv-share-email { background: #64748b15; color: #64748b; }
  .wv-share-email:hover { background: #64748b30; }
  .wv-share-copy { background: #f3efe6; color: var(--text-muted); }
  .wv-share-copy:hover { background: #ebe6d9; }

  /* Related Posts */
  .wv-related {
    max-width: 960px; margin: 0 auto;
    padding: 48px 32px 60px;
  }
  .wv-related-title {
    font-family: Georgia, serif;
    font-size: 28px; font-weight: 500;
    letter-spacing: -0.5px; margin-bottom: 28px;
  }
  .wv-related-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
  .wv-related-card {
    display: block; text-decoration: none; color: var(--text);
    border-radius: 16px; overflow: hidden;
    background: #fff; border: 0.5px solid var(--border);
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .wv-related-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.08);
  }
  .wv-related-img { height: 160px; overflow: hidden; }
  .wv-related-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
  .wv-related-card:hover .wv-related-img img { transform: scale(1.05); }
  .wv-related-ph {
    width: 100%; height: 100%;
    background: linear-gradient(135deg, #da251d, #ff6b4a);
    display: flex; align-items: center; justify-content: center;
    font-size: 48px; font-weight: 800; color: rgba(255,255,255,0.3);
  }
  .wv-related-body { padding: 16px; }
  .wv-related-tag {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--accent);
    margin-bottom: 6px; display: inline-block;
  }
  .wv-related-body h4 {
    font-size: 16px; font-weight: 700; line-height: 1.3;
    margin-bottom: 6px;
    display: -webkit-box; -webkit-line-clamp: 2;
    -webkit-box-orient: vertical; overflow: hidden;
  }
  .wv-related-body p {
    font-size: 13px; color: var(--text-muted); line-height: 1.5;
    margin-bottom: 8px;
    display: -webkit-box; -webkit-line-clamp: 2;
    -webkit-box-orient: vertical; overflow: hidden;
  }
  .wv-related-meta {
    font-size: 11px; color: var(--text-soft);
  }

  .wv-blog-footer {
    border-top: 0.5px solid var(--border);
    background: #ffffff;
  }
  .wv-blog-footer-policies {
    max-width: 900px; margin: 0 auto;
    padding: 16px 32px 0;
    display: flex; align-items: center; justify-content: center;
    flex-wrap: wrap; gap: 8px;
  }
  .wv-blog-footer-policies a {
    font-size: 12px; color: var(--text-soft);
    text-decoration: none; transition: color 0.15s;
  }
  .wv-blog-footer-policies a:hover { color: var(--accent); }
  .wv-foot-dot {
    width: 3px; height: 3px; border-radius: 50%;
    background: var(--border); flex-shrink: 0;
  }
  .wv-blog-footer-inner {
    max-width: 900px; margin: 0 auto;
    padding: 20px 32px;
    display: flex; justify-content: space-between;
    font-size: 12px; color: var(--text-soft);
  }

  @media (max-width: 768px) {
    .wv-blog-nav-inner { padding: 12px 16px; }
    .wv-blog-logo { font-size: 17px; gap: 6px; }
    .wv-blog-logo-mark { width: 24px; height: 24px; font-size: 12px; }
    .wv-powered-by { font-size: 9px; }
    .wv-blog-back-link { display: none; }
    .wv-blog-menu-btn { font-size: 11px; padding: 6px 12px; }

    .wv-blog-cover { padding: 16px 16px 0; }
    .wv-blog-cover img { height: 240px; border-radius: 12px; }
    .wv-blog-cover-fade { left: 16px; right: 16px; }

    .wv-blog-header h1 { font-size: 28px; letter-spacing: -0.5px; }
    .wv-blog-article { padding: 24px 16px 40px; }
    .wv-blog-body { font-size: 16px; line-height: 1.75; }
    .wv-blog-body h2 { font-size: 22px; }
    .wv-blog-body h3 { font-size: 18px; }
    .wv-blog-avatar { width: 36px; height: 36px; font-size: 12px; }
    .wv-blog-author { font-size: 14px; }
    .wv-blog-date { font-size: 12px; }
    .wv-blog-footer-inner { padding: 16px; flex-direction: column; gap: 6px; text-align: center; }

    .wv-share-buttons { gap: 8px; }
    .wv-share-btn { padding: 8px 14px; font-size: 12px; }
    .wv-related { padding: 32px 16px 40px; }
    .wv-related-title { font-size: 22px; }
    .wv-related-grid { grid-template-columns: 1fr; gap: 16px; }
    .wv-related-card { display: flex; flex-direction: row; }
    .wv-related-img { width: 120px; height: auto; min-height: 100px; flex-shrink: 0; }
    .wv-related-body { padding: 12px; flex: 1; }
  }
`;

export default BlogDetail;
